import { useState } from 'react'
import type { OutlineEntry, VersionInfo } from '@/lib/types/report'
import { sectionTitleMap, shortTime, versionTitleLabel } from '@/lib/report/versionDiff'
import { cn } from '@/lib/utils'

interface ActivityFeedProps {
  versions: VersionInfo[]
  outline: OutlineEntry[]
  selected: number
  onSelect: (version: number) => void
}

/** Collapsible change log — viewer chrome, hidden in print. Newest first; clicking an
 *  entry scrubs to that version and scrolls to its section once the snapshot mounts. */
export function ActivityFeed({ versions, outline, selected, onSelect }: ActivityFeedProps) {
  const [open, setOpen] = useState(false)
  if (versions.length === 0) return null
  const titles = sectionTitleMap(outline)
  const newestFirst = [...versions].reverse()

  const pick = (v: VersionInfo) => {
    setOpen(false)
    onSelect(v.version)
    if (v.change.type === 'section_write' && v.change.section_id) {
      const id = v.change.section_id
      // let the (possibly snapshot-fetched) content mount before scrolling
      setTimeout(() => {
        document
          .querySelector(`[data-section-id="${CSS.escape(id)}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 350)
    }
  }

  return (
    <div className="print:hidden relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground transition-colors hover:border-[var(--report-accent)]/50 hover:text-foreground"
      >
        Activity · {versions.length}
      </button>
      {open && (
        <ol
          aria-label="Report activity"
          className="absolute right-0 top-full z-20 mt-1 max-h-72 w-72 overflow-auto rounded-md border border-border bg-background py-1 shadow-lg"
        >
          {newestFirst.map((v) => (
            <li key={v.version}>
              <button
                type="button"
                onClick={() => pick(v)}
                aria-current={v.version === selected ? 'true' : undefined}
                className={cn(
                  'flex w-full items-baseline justify-between gap-2 px-3 py-1.5 text-left text-xs hover:bg-muted/40',
                  v.version === selected ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                <span className="truncate">{versionTitleLabel(v, titles)}</span>
                <span className="shrink-0 tabular-nums text-muted-foreground/70">{shortTime(v.created_at)}</span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
